import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  isMobileViewport,
  findHeroBottom,
  shouldUseLiquidHeader,
} from '../../utils/liquidHeader';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Heart, LayoutDashboard, LogIn, LogOut, UserPlus, Minus, Plus, Trash2 } from 'lucide-react';
import brand from '../../config/brand';
import { useAccountData } from '../../context/AccountContext';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageSwitcher from '../LanguageSwitcher';
import '../../styles/CateringHeader.css';

/** Menu lines are CNY; legacy catalogue lines fall back to USD. */
function money(value, currency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'CNY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(n);
  }
}

const CateringHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth, token, logout } = useAccountData() || {};
  const { cart, updateQuantity, removeFromCart } = useCart() || {};
  const { favorites } = useFavorites() || { favorites: [] };
  const { theme, toggleTheme } = useTheme() || {};
  const [cartOpen, setCartOpen] = useState(false);
  const cartRef = useRef(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const signedIn = Boolean(auth && token);

  // Mobile-only "liquid glass" header: floats in as a frosted pill after the
  // hero section scrolls out of view (see utils/liquidHeader.js).
  const [isLiquid, setIsLiquid] = useState(false);
  const location = useLocation();

  const lines = useMemo(() => (Array.isArray(cart) ? cart : []), [cart]);
  const cartCount = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l.quantity) || 1), 0),
    [lines],
  );
  const cartTotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) =>
          sum + (Number.isFinite(Number(l.priceValue)) ? Number(l.priceValue) : 0) * (Number(l.quantity) || 1),
        0,
      ),
    [lines],
  );
  const cartCurrency = lines[0]?.currency || 'CNY';

  const displayName =
    auth?.firstName || auth?.name?.split(' ')[0] || (auth?.email ? auth.email.split('@')[0] : '');
  const initial = (displayName?.[0] || '?').toUpperCase();

  // Close the cart popover on outside click / Escape.
  useEffect(() => {
    if (!cartOpen) return undefined;
    const onDown = (e) => {
      if (cartRef.current && !cartRef.current.contains(e.target)) setCartOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setCartOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [cartOpen]);

  const goCheckout = useCallback(() => {
    setCartOpen(false);
    if (lines.length > 0) navigate('/checkout');
  }, [navigate, lines.length]);

  // Close the account dropdown on outside click / Escape.
  useEffect(() => {
    if (!accountOpen) return undefined;
    const onDown = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  // Liquid-glass mode: re-evaluated on scroll/resize (rAF-throttled) and on
  // every route change, since each page has a different hero element.
  useEffect(() => {
    let ticking = false;
    const evaluate = () => {
      ticking = false;
      setIsLiquid(
        shouldUseLiquidHeader({
          mobile: isMobileViewport(
            typeof window !== 'undefined' ? window.matchMedia : undefined
          ),
          scrollY: window.scrollY,
          heroBottom: findHeroBottom(
            document,
            window.scrollY,
            window.innerHeight
          ),
        })
      );
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(evaluate);
      }
    };
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (ticking) window.cancelAnimationFrame(ticking);
    };
  }, [location.key]);

  const handleSignOut = useCallback(() => {
    setAccountOpen(false);
    if (typeof logout === 'function') {
      logout();
    } else {
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      } catch {
        /* storage unavailable */
      }
    }
    navigate('/', { replace: true });
  }, [logout, navigate]);

  const bump = useCallback(
    (line, delta) => {
      const next = Math.max(1, (Number(line.quantity) || 1) + delta);
      updateQuantity(line.id, next);
    },
    [updateQuantity],
  );

  return (
    <header className={`ck-nav${isLiquid ? ' liquid' : ''}`}>
      <div className="ck-nav__inner">
        {isLiquid ? (
          <button
            type="button"
            className="ck-nav__brand ck-nav__brand--liquid"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label={t('catering.nav.backToTop', 'Back to top')}
          >
            <img src="/favicon.svg" alt="" width={26} height={26} aria-hidden="true" />
          </button>
        ) : (
          <Link to="/" className="ck-nav__brand">
            <img src="/favicon.svg" alt="" width={26} height={26} aria-hidden="true" />
            <span className="ck-nav__brand-text">
              <strong>{brand.siteName}</strong>
              <small>{t('catering.nav.tagline', 'Private chefs & curated catering')}</small>
            </span>
          </Link>
        )}

        <nav className="ck-nav__links" aria-label={t('catering.nav.aria', 'Main')}>
          <Link to="/">{t('catering.nav.menu', 'Menu')}</Link>
          <Link to="/dashboard?tab=favorites" className="ck-nav__link-with-count">
            <Heart size={15} aria-hidden="true" />
            {t('catering.nav.favorites', 'Favorites')}
            {favorites?.length ? <span className="ck-nav__count">{favorites.length}</span> : null}
          </Link>
          <Link to="/dashboard?tab=orders">
            <LayoutDashboard size={15} aria-hidden="true" />
            {t('catering.nav.orders', 'Orders')}
          </Link>
        </nav>

        <div className="ck-nav__actions">
          <LanguageSwitcher />
          <button
            type="button"
            className="ck-nav__theme"
            onClick={toggleTheme}
            aria-label={t('catering.common.toggleTheme', 'Toggle theme')}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>

          <div className="ck-cart" ref={cartRef}>
            <button
              type="button"
              className={`ck-cart__btn${cartCount > 0 ? ' is-active' : ''}`}
              onClick={() => setCartOpen((v) => !v)}
              aria-label={t('catering.cart.aria', 'Cart')}
              aria-expanded={cartOpen}
            >
              <ShoppingCart size={18} aria-hidden="true" />
              {cartCount > 0 && <span className="ck-cart__badge">{cartCount}</span>}
            </button>

            {cartOpen && (
              <div className="ck-cart__panel" role="menu">
                {lines.length === 0 ? (
                  <p className="ck-cart__empty">
                    {t('catering.cart.empty', 'Your cart is empty. Add dishes from the menu.')}
                  </p>
                ) : (
                  <>
                    <ul className="ck-cart__list">
                      {lines.map((line) => (
                        <li key={line.id} className="ck-cart__item">
                          {line.imageUrl ? (
                            <img
                              className="ck-cart__thumb"
                              src={line.imageUrl}
                              alt=""
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <span className="ck-cart__thumb ck-cart__thumb--empty" aria-hidden="true" />
                          )}
                          <div className="ck-cart__info">
                            <span className="ck-cart__name">{line.title || line.name || line.id}</span>
                            <span className="ck-cart__price">
                              {money(line.priceValue, line.currency || cartCurrency)}
                            </span>
                          </div>
                          <div className="ck-cart__qty">
                            <button
                              type="button"
                              onClick={() => bump(line, -1)}
                              aria-label={t('common.decrease', 'Decrease')}
                              disabled={(Number(line.quantity) || 1) <= 1}
                            >
                              <Minus size={12} aria-hidden="true" />
                            </button>
                            <span>{Number(line.quantity) || 1}</span>
                            <button
                              type="button"
                              onClick={() => bump(line, 1)}
                              aria-label={t('common.increase', 'Increase')}
                            >
                              <Plus size={12} aria-hidden="true" />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="ck-cart__remove"
                            onClick={() => removeFromCart(line.id)}
                            aria-label={t('catering.cart.remove', 'Remove')}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="ck-cart__footer">
                      <div className="ck-cart__total">
                        <span>
                          {t('catering.cart.count', '{{count}} items', { count: lines.length })}
                        </span>
                        <strong>{money(cartTotal, cartCurrency)}</strong>
                      </div>
                      <button type="button" className="ck-btn ck-btn--primary ck-btn--sm" onClick={goCheckout}>
                        {t('catering.cart.checkout', 'Checkout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {signedIn ? (
            <div className="ck-account-menu" ref={accountRef}>
              <button
                type="button"
                className="ck-nav__profile"
                onClick={() => setAccountOpen((v) => !v)}
                aria-label={t('catering.nav.account', 'Account')}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                <span className="ck-nav__avatar">{initial}</span>
                <span className="ck-nav__profile-name">{displayName}</span>
              </button>
              {accountOpen && (
                <div className="ck-account-menu__panel" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="ck-account-menu__item"
                    onClick={() => {
                      setAccountOpen(false);
                      navigate('/dashboard');
                    }}
                  >
                    <LayoutDashboard size={15} aria-hidden="true" />
                    {t('catering.nav.dashboard', 'Dashboard')}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="ck-account-menu__item ck-account-menu__item--danger"
                    onClick={handleSignOut}
                  >
                    <LogOut size={15} aria-hidden="true" />
                    {t('catering.common.signOut', 'Sign out')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="ck-btn">
                <LogIn size={15} aria-hidden="true" />
                {t('catering.common.signIn', 'Sign in')}
              </Link>
              <Link to="/register" className="ck-btn ck-btn--primary">
                <UserPlus size={15} aria-hidden="true" />
                {t('catering.nav.createAccount', 'Create account')}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default CateringHeader;
